import React from "react";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";

type Props = {
	/** Single permission key required */
	permission?: string;
	/** Any of these keys grant access */
	anyOf?: string[];
	/** All of these keys required */
	allOf?: string[];
	/** Rendered when permission denied (default: nothing) */
	fallback?: React.ReactNode;
	children: React.ReactNode;
};

/**
 * Conditionally render children based on RBAC permission.
 *
 * Usage:
 *   <PermissionGate permission="orders.refund">
 *     <button>Refund</button>
 *   </PermissionGate>
 *
 *   <PermissionGate anyOf={["orders.edit", "orders.cancel"]}>
 *     <ActionsMenu />
 *   </PermissionGate>
 *
 * While permissions are still loading, returns fallback (hides children) to avoid
 * showing a button the user can't actually click.
 */
export default function PermissionGate({ permission, anyOf, allOf, fallback = null, children }: Props) {
	const { hasPermission, hasAny, loaded } = useAdminPermissions();
	if (!loaded) return <>{fallback}</>;
	let allowed = true;
	if (permission) allowed = hasPermission(permission);
	else if (anyOf?.length) allowed = hasAny(anyOf);
	else if (allOf?.length) allowed = allOf.every((k) => hasPermission(k));
	return <>{allowed ? children : fallback}</>;
}
